import { makeAutoObservable, runInAction, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { logger, toLocalISOString } from 'shared/lib'
import { type ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { RequestLoadData } from 'shared/api/lib'
import { WebhookApi } from 'entities/Webhook/api/webhook'
import {
  type IWebhookHistoryStatusesKeys,
  type IWebhookHistoryRow,
  type IParamsGetWebhookHistory,
  type IResponseWebhookHistoryItem,
  type IResponseExternalWebhook,
} from 'entities/Webhook'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { HistoryDetailsModalContent } from 'pages/settings/pages/integrations/pages/Webhooks/ui/HistoryDetailsModalContent/HistoryDetailsModalContent'

export class WebhookHistoryStore {
  private _success = 0
  private _failed = 0
  private _items: IWebhookHistoryRow[] = []
  private _activeFilterKey: IWebhookHistoryStatusesKeys = 'total'
  private _currentWebhook: IResponseExternalWebhook | null = null
  private _disposeParamsReaction: IReactionDisposer | null = null
  private _modalId = nanoid()
  private _loading = false
  private _calendarFilterStore: CalendarFilterStore

  private _tableStore: TableStore<IWebhookHistoryRow>

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore<IWebhookHistoryRow>({
      key: 'webhook_history',
      element: 'webhook_history',
      useColumnsCache: false,
      limit: 10,
      page: 1,
      sortBy: 'created_at',
      sortOrder: 'desc',
    })

    this._calendarFilterStore = new CalendarFilterStore(null)
  }

  updateWebhook = (webhook: IResponseExternalWebhook) => {
    runInAction(() => {
      this._currentWebhook = webhook
    })
  }

  loadWebhook = async (webhookId: number) => {
    runInAction(() => {
      this._loading = true
    })

    try {
      const { data } = await WebhookApi.getExternalWebhookById(webhookId)

      this.updateWebhook(data)
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async (webhookId: number) => {
    this.getWebhookHistory(webhookId)
    await this.loadWebhook(webhookId)

    this._currentWebhook &&
      this._calendarFilterStore.updateCreatedAt(this._currentWebhook?.created_at)
    this._calendarFilterStore.setFromFilterDefaultDate()

    this._setupParamsReaction()
  }

  private _setupParamsReaction = () => {
    this._disposeParamsReaction?.()
    this._disposeParamsReaction = reaction(
      () => ({
        page: this._tableStore.page,
        limit: this._tableStore.limit,
        sortBy: this._tableStore.sortBy,
        sortOrder: this._tableStore.sortOrder,
      }),
      () => {
        if (this._currentWebhook) {
          this._requestLoadWebhookHistory.debounceLoadData(this._currentWebhook.id)
        }
      }
    )
  }

  private _requestLoadWebhookHistory = new RequestLoadData<number>({
    debounceDelay: 300,
    requestCallback: async (config, webhookId) => {
      if (!webhookId) return

      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const params: IParamsGetWebhookHistory = {
        page: this._tableStore.page,
        limit: this._tableStore.limit,
        sortBy: this._tableStore.sortBy,
        sortOrder: this._tableStore.sortOrder,
      }

      if (this._activeFilterKey !== 'total') {
        params.statuses = [this._activeFilterKey]
      }

      const sendDate = this._calendarFilterStore.sendDate

      if (sendDate?.from && sendDate.to) {
        // back waits YYYY-MM-DD format
        params.from = toLocalISOString(sendDate.from)?.split('T')[0]
        params.to = toLocalISOString(sendDate.to)?.split('T')[0]
        params.timezone = this._entities.usersStore.user?.timezone || ''
      }

      const response = await WebhookApi.getWebhookHistory(webhookId, params)

      runInAction(() => {
        this._tableStore.setTotal(response.data.meta.total)
        this._items = response.data.data.map((item) => this._transformToRow(item))
        this._success = response.data.counts.success_total
        this._failed = response.data.counts.failed_total
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    },
  })

  getWebhookHistory = async (webhookId: number) => {
    return this._requestLoadWebhookHistory.loadData(webhookId)
  }

  onCloseCalendar = () => {
    this._tableStore.setPage(1)

    if (this._currentWebhook) {
      this.getWebhookHistory(this._currentWebhook.id)
    }
  }

  private _transformToRow = (item: IResponseWebhookHistoryItem): IWebhookHistoryRow => {
    return {
      id: item.id,
      loading: false,
      actions: null,
      isDisabled: false,
      isCheckboxDisabled: false,
      ariaLabel: `Webhook history ${item.id}`,
      columnsKeys: ['event_key', 'request_url', 'status', 'created_at'],
      setColumnsKeys: () => {},
      fields: {
        eventKey: item.event_key,
        requestUrl: item.request_url,
        status: item.status,
        createdAt: item.created_at,
        responseBody: item.response_body,
        responseCode: item.response_code,
        requestParams: item.request_params,
        exceptionInfo: item.exception_info,
        retryCount: item.retry_count,
      },
      withActions: false,
      actionsProps: null,
      expanded: false,
      is_can_expanded: false,
      isMoreExpand: false,
      loadingMore: false,
    }
  }

  setActiveFilterKey = (key: IWebhookHistoryStatusesKeys) => {
    this._activeFilterKey = key
    this._tableStore.setPage(1)
    this._currentWebhook && this.getWebhookHistory(this._currentWebhook.id)
  }

  openDetailsModal = (row: IWebhookHistoryRow) => {
    modalStore.addModal({
      id: this._modalId,
      position: 'right',
      width: 540,
      showCloseButton: false,
      title: 'Details',
      fullHeight: true,
      pureContent: true,
      type: ModalTypeList.DEFAULT,
      ModalContentProps: { history: row, webhook: this._currentWebhook },
      ModalContent: HistoryDetailsModalContent,
    })
  }

  onRowClick = (row: IWebhookHistoryRow) => {
    this.openDetailsModal(row)
  }

  get tableStore() {
    return this._tableStore
  }

  get activeFilterKey() {
    return this._activeFilterKey
  }

  get generalTotal() {
    return this._success + this._failed
  }

  get cardsFilters(): ICardFilter[] {
    return [
      {
        key: 'total',
        title: 'Total',
        amount: this.generalTotal,
        active: this.activeFilterKey === 'total',
      },
      {
        key: 'success',
        title: 'Success',
        amount: this._success,
        percentage:
          this.generalTotal === 0 ? 0 : Math.floor((this._success / this.generalTotal) * 100),
        active: this.activeFilterKey === 'success',
      },
      {
        key: 'failed',
        title: 'Failed',
        amount: this._failed,
        percentage:
          this.generalTotal === 0 ? 0 : Math.ceil((this._failed / this.generalTotal) * 100),
        active: this.activeFilterKey === 'failed',
      },
    ]
  }

  get total() {
    return this._tableStore.total
  }

  get items() {
    return this._items
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get webhook() {
    return this._currentWebhook
  }

  get isLoading() {
    return this._loading
  }

  dispose = () => {
    this._disposeParamsReaction?.()
    this._tableStore.dispose()
    this._requestLoadWebhookHistory.dispose()
  }
}
